import React, { useState } from 'react'
import clsx from 'classnames'
import { Link, useNavigate } from 'react-router'
import type { ServiceGroup } from 'src/store/data.store'
import { getServiceTypeIcon } from 'utils/getServiceTypeIcon'
import { parseHtml } from 'utils/htmlParserWithCustomLinks'
import { formatPrice } from 'utils/formatPrice'
import { AppButton } from 'components/FormElements/AppButton/AppButton'
import { ServiceOptionsModal } from 'components/PageElements/ServiceOptionsModal/ServiceOptionsModal'
import styles from './ServiceInfo.module.scss'

type ServiceInfoProps = {
  /** A service page with its options; with several, the card shows "от" the cheapest and asks which to book */
  group: ServiceGroup,
  /** Called before opening booking with the service (the picked option) */
  onSelect: (serviceId: number) => void,
  noLink?: boolean,
  /** On the service's own page: the title is the h1 and the long description is shown */
  asPageInfo?: boolean,
  className?: string,
}

/** A service card: name, price, duration, description and a booking button */
export const ServiceInfo: React.FC<ServiceInfoProps> = (props) => {
  const { group, onSelect, noLink = false, asPageInfo = false, className } = props
  const navigate = useNavigate()
  const [isOptionsModalOpen, setIsOptionsModalOpen] = useState(false)
  const service = group.options[0].service
  const hasOptions = group.options.length > 1
  const minPrice = Math.min(...group.options.flatMap((option) => option.service.cityPrices))
  const durations = group.options.map((option) => option.service.duration)
  const minDuration = Math.min(...durations)
  const price = hasOptions ? (Number.isFinite(minPrice) ? `от ${minPrice}` : '-') : formatPrice(service)
  const duration = hasOptions && durations.some((d) => d !== minDuration)
    ? `от ${minDuration} мин`
    : `${service.duration} мин`

  const book = (serviceId: number) => {
    setIsOptionsModalOpen(false)
    onSelect(serviceId)
    navigate('#appointment')
  }
  const meta = group.page
  const pageVars = meta?.templateVars?.pageVars ?? null
  const description = pageVars ? parseHtml(pageVars.description || '') : ''
  const altDescription = pageVars ? parseHtml(pageVars.altDescription || '') : ''
  const title = meta?.title
  const linkToService = meta ? meta.fullPath : ''

  return (
    <div data-type={pageVars?.type} data-id={service.id} className={clsx([styles.container, className])}>
      <div className={styles.info}>
        <div className={styles.name}>
          {asPageInfo
            ? <h1 className={styles.title}>{title}</h1>
            : <span className={styles.title}>{title}</span>}
          <span />
          <span className={styles.typeIcon}>
            <img src={getServiceTypeIcon(service.type)} alt={''} />
          </span>
        </div>
        <div className={styles.mainInfo}>
          <div className={styles.priceContainer}>
            <span className={styles.price}>{price}</span>
            <span className={styles.currency}>₽</span>
          </div>
          <div className={styles.durationContainer}>
            <i className={styles.durationIcon} />
            <div className={styles.duration}>{duration}</div>
          </div>
        </div>
        <div className={styles.descriptionContainer}>
          {(!!description && !asPageInfo) && <div className={clsx([styles.description, 'text-content'])}>
            {description}
          </div>}
          {(!!altDescription && asPageInfo) && <div className={clsx([styles.altDescription, 'text-content'])}>
            {altDescription}
          </div>}
          {!noLink && <Link to={linkToService} className={styles.linkToService}>
            Подробнее
          </Link>}
        </div>
      </div>
      <div className={styles.action}>
        <AppButton
          onClick={() => (hasOptions ? setIsOptionsModalOpen(true) : book(service.id))}
        >
          Записаться
        </AppButton>
      </div>
      {hasOptions && (
        <ServiceOptionsModal
          group={group}
          isOpen={isOptionsModalOpen}
          onClose={() => setIsOptionsModalOpen(false)}
          onSelect={book}
        />
      )}
    </div>
  )
}
